// fetchDecryptedResource.js
'use strict';
import fetch from '../resource/customFetch.js';
import handleFetchError from '../resource/handleFetchError.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import responseDecryptionUtil, {
  unwrapAesKey,
} from '../resource/responseDecryptionUtil.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import getMeData from '../selector/getMeData.js';

import {
  MERGE_DECRYPTED_RESOURCE_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

/**
 * Fetch encrypted resource, decrypt it, and then create a new Blob.
 * @kind action
 * @param {string} {encryptedResourceUrl} - the encrypted resource url.
 * @return {Promise} Action promise.
 */
const fetchDecryptedResource =
  ({ encryptedResourceUrl }) =>
  async (dispatch, getState) => {
    const selectPath = ['decryptedResources', encryptedResourceUrl];
    const fetchOptions = {
      method: 'GET',
      headers: getHeaders(),
      // use native fetch to get the encrypted resource, we need the encrypted data and then decrypt it in this action
      shouldUseCustomFetch: false,
    };

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    try {
      let response = await fetch(encryptedResourceUrl, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchUrl: encryptedResourceUrl,
        });
      }

      const { buffer, stream, contentType } = await responseDecryptionUtil({
        response,
        preferredReturnType: 'readableStream',
      });

      let blob = null;
      if (stream) {
        blob = await new Response(stream, {
          headers: {
            'content-type': contentType,
          },
        }).blob();
      } else {
        blob = new Blob([buffer], { type: contentType });
      }
      const objectUrl = URL.createObjectURL(blob);

      dispatch({
        type: MERGE_DECRYPTED_RESOURCE_DATA,
        payload: {
          selectPath: [encryptedResourceUrl],
          data: {
            resourceUrl: encryptedResourceUrl,
            type: contentType,
            objectUrl,
          },
        },
      });
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

/**
 * Fetch AES-CBC encrypted image, decrypt with key API, and store as Blob.
 * @kind action
 * @param {string} {encryptedResourceUrl} - AES image encrypted resource url.
 * @param {string} {messageId} - message id for key API.
 * @param {string} {assetId} - asset id for key API.
 * @return {Promise} Action promise.
 */
export const fetchDecryptedAesImageResource =
  ({ encryptedResourceUrl, messageId, assetId }) =>
  async (dispatch, getState) => {
    const selectPath = ['decryptedResources', encryptedResourceUrl];

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    try {
      const clientId = getMeData(getState(), 'clientId');
      const token = getMeData(getState(), 'token');
      const sessionId = getMeData(getState(), 'sessionId');
      const headers = {
        ...getHeaders(),
        ...(clientId && { 'X-Client-ID': clientId }),
        ...(sessionId && { 'X-Session-ID': sessionId }),
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const decryptionKeyUrl = getResourceUrl({
        endpoint: `/messages/${messageId}/assets/${assetId}/key`,
      }).href;

      // Bypass customFetch to get raw encrypted binary for manual AES-CBC decryption
      let encryptedResponse = await fetch(encryptedResourceUrl, {
        method: 'GET',
        headers: getHeaders(),
        shouldUseCustomFetch: false,
      });
      if (!encryptedResponse.ok) {
        encryptedResponse = await handleFetchError({
          response: encryptedResponse,
          dispatch,
          getState,
          fetchUrl: encryptedResourceUrl,
        });
      }

      // Bypass customFetch since auth headers are manually constructed above
      let decryptionKeyResponse = await fetch(decryptionKeyUrl, {
        method: 'GET',
        headers,
        shouldUseCustomFetch: false,
      });
      if (!decryptionKeyResponse.ok) {
        decryptionKeyResponse = await handleFetchError({
          response: decryptionKeyResponse,
          dispatch,
          getState,
          fetchUrl: decryptionKeyUrl,
        });
      }

      // Decrypt transport-level AES-CBC encryption if present(for china)
      const { buffer } = await responseDecryptionUtil({
        response: decryptionKeyResponse,
      });
      const keyResponseData = JSON.parse(new TextDecoder().decode(buffer));
      const { nonce, wrapped_key: wrappedKey } = keyResponseData;
      const rawKey = await unwrapAesKey({
        clientId,
        sessionId,
        nonce,
        wrappedKey,
      });

      // AES-CBC decrypt: key = iv = rawKey
      const subtleCrypto =
        globalThis.crypto.subtle || globalThis.crypto.webkitSubtle;
      const cryptoKey = await subtleCrypto.importKey(
        'raw',
        rawKey,
        { name: 'AES-CBC' },
        false,
        ['decrypt']
      );
      // Strip transport-level AES-CBC encryption when backend applies it.
      const { buffer: encryptedData } = await responseDecryptionUtil({
        response: encryptedResponse,
      });
      const decryptedData = await subtleCrypto.decrypt(
        { name: 'AES-CBC', iv: new Uint8Array(rawKey) },
        cryptoKey,
        encryptedData
      );

      const contentType =
        encryptedResponse.headers.get('content-type') || 'image/jpeg';
      const blob = new Blob([decryptedData], { type: contentType });
      const objectUrl = URL.createObjectURL(blob);

      dispatch({
        type: MERGE_DECRYPTED_RESOURCE_DATA,
        payload: {
          selectPath: [encryptedResourceUrl],
          data: {
            resourceUrl: encryptedResourceUrl,
            type: contentType,
            objectUrl,
          },
        },
      });
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default fetchDecryptedResource;
